import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import s from './Model.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialModelState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { Navigate, Link, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Checkbox from '../WSAControls/CheckboxContainer/Checkbox';
import GoBack from '../WSAControls/GoBack/GoBack';
import classNames from 'classnames';
import { isRequired } from '../../utils';
import { v4 as uuid } from 'uuid';
import Cookies from 'js-cookie';
import { IsWidgetAccessible } from '../../utils/widgetManager';
import Table from 'react-bootstrap/Table';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import { compareValues } from '../../utils/';
import ModelCategoryConfigurationContentTemplate from '../ModelCategoryTab/ModelCategoryConfigurationContentTemplate';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb'
import ModernCheckbox from '../WSAControls/ModernCheckbox/ModernCheckbox';
import { JsonEditor } from 'json-edit-react';


const Model = ({
  saveModel,
  describeModel,
  getModelVariants,
  setDuplicateChange,
  modelManager = {
    ...initialModelState.modelManager
  },
  flowSequences,
  changeModel,
  modelCleanUp,
  userProfileWidgets,
  intl
}) => {
  let IsEdit = false;
  let IsDuplicate = false;
  let IsRedirect = false;
  let heading = '';
  const [localformErrors, setFormErrors] = useState({});
  const [searchText, setSearchText] = useState('');
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  const isDuplicateAllowed = IsWidgetAccessible(userProfileWidgets, 'MODEL_DUPLICATE');
  const organisationId = Cookies.get(`selectedorganisationid-${envName}`) || 0;

  let { ModelId, Duplicate, ModelCategoryId } = useParams();

  const jsonEditorTheme = {
    displayName: 'Default',
    fragments: { edit: 'rgb(42, 161, 152)' },
    styles: {
      container: {
        fontFamily: '"Barlow", Helvetica, Arial, sans-serif',
        maxWidth: '100%'
      }
    }
  };


  // Get Organisation data from modelManager (assuming it's stored there)
  const Organisations = modelManager.selectedModel.ModelCategoryOrganisations || [];

  let widgetCode = 'MODEL_ADD';
  if (ModelId > 0) {
    IsEdit = true;
    if (Duplicate) {
      widgetCode = 'MODEL_DUPLICATE';
      IsDuplicate = true;
      heading = intl.formatMessage({ id: 'model.duplicateModel', defaultMessage: 'Duplicate' });
    } else {
      widgetCode = 'MODEL_UPDATE';
      heading = intl.formatMessage({ id: 'model.editModel', defaultMessage: 'Edit' });
    }
  } else {
    heading = intl.formatMessage({ id: 'model.addModel', defaultMessage: 'Add Model' });
    IsEdit = false;
  }

  useEffect(() => {
    if (ModelId) {
      if (Duplicate) {
        describeModel(ModelId, Duplicate, widgetCode, ModelCategoryId);
      } else {
        describeModel(ModelId, null, widgetCode, ModelCategoryId);
      }
    }
  }, [describeModel, ModelId, Duplicate, widgetCode, ModelCategoryId]);

  useEffect(() => {
    if (ModelId && isDuplicateAllowed && IsDuplicate) {
      getModelVariants(ModelCategoryId, ModelId, 0, 1000, null, organisationId, true);
    } else if (!IsDuplicate) {
      modelCleanUp({
        ...initialModelState.modelManager
      });
    }
  }, [getModelVariants, ModelCategoryId, ModelId, isDuplicateAllowed, IsDuplicate]);

  //clean up
  useEffect(() => {
    return () => {
      modelCleanUp({
        ...initialModelState.modelManager
      });
    };
  }, []);

  // Disble selection check for provisioned Organisations as only one can be provisioned at a time
  let disableSelectionCheck = false;
  if (Organisations.filter(org => org.IsSelected && org.IsProvisioned == 0).length > 0) {
    disableSelectionCheck = true;
  } else {
    disableSelectionCheck = false;
  }



  // Transform Organisation data for ModernCheckbox
  const OrganisationCheckboxData = useMemo(() => {
    return Organisations.map(org => ({
      SKEY: org.OrganisationId,
      label: org.Name,
      Name: org.Name,
      ModelCategoryId: org.ModelCategoryId,
      IsProvisioned: org.IsProvisioned === 1 ? intl.formatMessage({ id: 'common.yes', defaultMessage: 'Yes' }) : intl.formatMessage({ id: 'common.no', defaultMessage: 'No' }),
      disable: org.IsProvisioned == 1,
      // Store original data for easy access
      _original: org
    }));
  }, [Organisations]);

  // Get currently provisioned Organisation IDs
  const provisionedOrgIds = useMemo(() => {
    return new Set(Organisations.filter(org => org.IsSelected).map(org => org.OrganisationId));
  }, [Organisations]);


  // Handle Organisation selection changes
  const handleOrganisationSelectionChange = (e, payload) => {
    let updatedOrganisations = [...Organisations];

    if (payload.isSelectAll) {
      // Handle select all / deselect all
      const affectedRows = payload.rows || [];
      const rowIds = new Set(affectedRows.map(row => row.SKEY));

      updatedOrganisations = updatedOrganisations.map(org => {
        if (rowIds.has(org.OrganisationId)) {
          return {
            ...org,
            IsSelected: payload.targetState ? 1 : 0
          };
        }
        return org;
      });
    } else {
      // Handle single row toggle
      const { row, checked } = payload;
      updatedOrganisations = updatedOrganisations.map(org => {
        if (org.OrganisationId === row.SKEY) {
          return {
            ...org,
            IsSelected: checked ? (disableSelectionCheck ? 0 : 1) : 0
          };
        }
        return org;
      });
    }

    // Update the local state
    setLocalModel({
      ...modelManager,
      selectedModel: {
        ...modelManager.selectedModel,
        ModelCategoryOrganisations: updatedOrganisations
      }
    });

  };

  const handleSearchChange = (value) => {
    setSearchText(value);
  };


  const onDropdownFilterFlowSequenceChange = e => {
    setDuplicateChange({
      ...modelManager.selectedModel.DuplicateModel,
      DefaultFlowSequenceId: e.value
    });
  };

  // Update redux store
  const setLocalModel = currentState => {
    changeModel(currentState);
  };
  //Saving
  const submitForm = e => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let saveData = modelManager.selectedModel;
    saveData.IsEdit = IsEdit;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveData.OrganisationId = Cookies.get(`selectedorganisationid-${envName}`) || 1;
    saveData.ModelCategoryId = ModelCategoryId || 0;

    if (!IsEdit) {
      saveData.ModelId = 0;
      saveData.GuidIdentifier = uuidv4();
    }
    if (IsDuplicate) {
      saveData.RefModelId = ModelId;
      saveData.IsDuplicate = IsDuplicate;
      saveData.GuidIdentifier = uuidv4();
    }
    //saving Model
    saveModel(saveData, widgetCode);
  };

  const validateInput = () => {
    let formErrors = {};
    let isValid = true;

    if (
      !modelManager ||
      !modelManager.selectedModel ||
      !modelManager.selectedModel.Name ||
      isRequired(modelManager.selectedModel.Name, 1)
    ) {
      formErrors.Name = intl.formatMessage({
        id: 'model.nameMandatory',
        defaultMessage: 'Model name is a mandatory field'
      });
      isValid = false;
    }

    setFormErrors(formErrors);

    return isValid;
  };

  const onDuplicateChange = e => {
    if (e.target.name === 'ComponentPositionLinks' || e.target.name === 'ModelSetpoints') {
      let modelVariantsData = modelManager.selectedModel.DuplicateModel.ModelVariants || [];
      modelVariantsData = modelVariantsData.map(obj => ({ ...obj, ModelVariantThresholds: false }));
      if (e.target.name === 'ModelSetpoints') {
        modelVariantsData = modelVariantsData.map(obj => ({ ...obj, ModelVariantSetpoints: false }));
      }
      let obj = {};

      if (e.target.name === 'ComponentPositionLinks') {
        obj.ModelTelemetryLinks = false;
      }

      setDuplicateChange({
        ...modelManager.selectedModel.DuplicateModel,
        [e.target.name]: e.target.checked,
        ModelThresholds: false,
        ModelVariants: modelVariantsData,
        ...obj
      });
    }
    else if (e.target.name === 'ModelThresholds') {
      let modelVariantsData = modelManager.selectedModel.DuplicateModel.ModelVariants || [];
      modelVariantsData = modelVariantsData.map(obj => ({ ...obj, ModelVariantThresholds: false }));
      setDuplicateChange({
        ...modelManager.selectedModel.DuplicateModel,
        [e.target.name]: e.target.checked,
        ModelVariants: modelVariantsData
      });
    }
    else {
      setDuplicateChange({
        ...modelManager.selectedModel.DuplicateModel,
        [e.target.name]: e.target.checked
      });
    }
  };

  //on control value change
  const onChange = e => {
    if (e.target && e.target.type && e.target.type === 'checkbox') {
      setLocalModel({
        ...modelManager,
        selectedModel: { ...modelManager.selectedModel, [e.target.name]: e.target.checked }
      });
    } else {
      setLocalModel({
        ...modelManager,
        selectedModel: { ...modelManager.selectedModel, [e.target.name]: e.target.value }
      });
    }
  };

  const onDropdownChange = e => {
    setLocalModel({
      ...modelManager,
      selectedModel: { ...modelManager.selectedModel, SchematicDiagramName: e.value }
    });
  };

  const onHardwareConfigDataChange = data => {
    setLocalModel({
      ...modelManager,
      selectedModel: {
        ...modelManager.selectedModel,
        HardwareConfigJsonText: (data.newData && JSON.stringify(data.newData)) || null
      }
    });
  };

  const onCommsConfigDataChange = data => {
    setLocalModel({
      ...modelManager,
      selectedModel: {
        ...modelManager.selectedModel,
        CommsConfigJsonText: (data.newData && JSON.stringify(data.newData)) || null
      }
    });
  };

  const schematicDiagramOptions = () => {
    let schematicDiagramOptions = [
      {
        label: 'Schematic diagram Ver 1',
        value: 'DeviceSchematicSvg'
      },
      {
        label: 'Schematic diagram Ver 2',
        value: 'DeviceSchematicSvg1'
      },
      {
        label: 'Alpha 2 Schematic diagram Ver 3',
        value: 'Alpha2DeviceSchematicSvg3'
      },
      {
        label: 'Alpha 2 Schematic diagram Ver 4',
        value: 'Alpha2DeviceSchematicSvg4'
      },
      {
        label: 'EleIot Gateway',
        value: 'EleIoTGatewaySvg'
      },
      {
        label: 'OZ Water Demo Rig',
        value: 'OZWaterDemoRigSvg'
      },
      {
        label: 'Alpha 2',
        value: 'alpha2Svg'
      },
      {
        label: 'PLCNext BASIC',
        value: 'PLCNextSvg'
      },
      {
        label: 'PLCNext DEMO',
        value: 'PLCNextDemoSvg'
      }
      ,
      {
        label: 'Algaesys Schematic Diagram',
        value: 'AlgaesysSvg'
      }
    ];

    return schematicDiagramOptions.sort(compareValues('label'));
  };

  let messageId = (modelManager && modelManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const duplicateModelName =
    (isDuplicateAllowed && IsDuplicate && modelManager.selectedModel ? modelManager.selectedModel.ModelName : '') || '';



  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: heading } // Last item has no href (current page)
  ];

  return (
    <div className={s.model}>
      {modelManager.isLoading && <LoadingSpinner />}
      {modelManager.isOpSuccessful && !modelManager.showBanner ? <Navigate to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-list`} /> : ''}
      {IsRedirect ? <Navigate to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-list`} /> : ''}
      <Banner
        key={uuid()}
        failureText={messageText}
        showBanner={modelManager.showBanner}
        status={modelManager.isOpSuccessful}
        successText={messageText}
      />

      <ModelCategoryConfigurationContentTemplate selectedPage="manageModels" userProfileWidgets={userProfileWidgets}>
        <div className={s.contentWrapper}>
          <div className={s.modelHeader}>

            {/* <GoBack className={s.backLink}>
              &lt; &nbsp;
              <FormattedMessage id="modelComponentTypes.back" defaultMessage="BACK" />
            </GoBack> */}
            <h3>{heading}</h3>

          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div className={s.modelContent}>
            <Form>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formModelName">
                    <Form.Label>
                      <FormattedMessage id="model.modelName" defaultMessage="Model Name" />
                    </Form.Label>

                    <Form.Control
                      type="text"
                      name="Name"
                      onChange={onChange}
                      value={modelManager.selectedModel.Name}
                      className={`${s.formControl} ${localformErrors && localformErrors.Name ? s.formControlError : ''}`}
                    />
                    {localformErrors && localformErrors.Name && (
                      <p role="alert" className={s.error}>
                        {localformErrors.Name}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formModelName">
                    <Form.Label>
                      <FormattedMessage id="model.schematicDiagram" defaultMessage="Schematic Diagram" />
                    </Form.Label>

                    <Dropdown
                      id="ddlComponentType"
                      dataArray={schematicDiagramOptions()}
                      controlData={{
                        placeholderText: <FormattedMessage id="common.selectOne" defaultMessage="Please select one" />
                      }}
                      onDropdownChange={onDropdownChange}
                      selectedOption={schematicDiagramOptions().filter(
                        option => option.value === modelManager.selectedModel.SchematicDiagramName
                      )}
                      data-unittest="ddlComponentType"
                      disabled={IsEdit && !IsDuplicate}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formModelDescription">
                    <Form.Label>
                      <FormattedMessage id="model.description" defaultMessage="Model Description" />
                    </Form.Label>

                    <Form.Control
                      as="textarea"
                      rows="3"
                      name="Description"
                      onChange={onChange}
                      placeholder={intl.formatMessage({
                        id: 'model.enterDescription',
                        defaultMessage: 'Enter description'
                      })}
                      value={modelManager.selectedModel.Description}
                      className={`${s.textArea} ${localformErrors && localformErrors.Description ? s.formControlError : ''}`}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={12}>
                  <Form.Group controlId="formHardwareConfigJsonText">
                    <Form.Label>
                      <FormattedMessage id="model.hardwareConfigData" defaultMessage="Hardware Config Data" />
                    </Form.Label>
                    <div className={s.jsonEditorContainer}>
                      <JsonEditor
                        className={s.jsonEditor}
                        theme={jsonEditorTheme}
                        data={
                          modelManager.selectedModel.HardwareConfigJsonText && JSON.parse(modelManager.selectedModel.HardwareConfigJsonText) || {}
                        }
                        onUpdate={onHardwareConfigDataChange}
                        collapse={true}
                      />
                    </div>

                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={12}>
                  <Form.Group controlId="formCommsConfigJsonText">
                    <Form.Label>
                      <FormattedMessage id="model.commsConfigData" defaultMessage="Communication Config Data" />
                    </Form.Label>
                    <div className={s.jsonEditorContainer}>
                      <JsonEditor
                        className={s.jsonEditor}
                        theme={jsonEditorTheme}
                        data={
                          modelManager.selectedModel.CommsConfigJsonText && JSON.parse(modelManager.selectedModel.CommsConfigJsonText) || {}
                        }
                        onUpdate={onCommsConfigDataChange}
                        collapse={true}
                      />
                    </div>

                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formActiveModel">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'IsActive',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.active',
                            defaultMessage: 'Model is active'
                          }),
                          isChecked: modelManager.selectedModel.IsActive
                        }
                      ]}
                      onSelectionChange={onChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>



          {isDuplicateAllowed && IsDuplicate && (
            <div className={s.modelContent}>
              <Row>
                <Col className={s.subHeading}>
                  <h4>
                    <FormattedMessage id="model.duplicateOptions" defaultMessage="Duplicate options" />
                  </h4>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formComponentPositionLinks">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ComponentPositionLinks',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateComponentPositionLinks',
                            defaultMessage: 'Component Position Links'
                          }),
                          isChecked: modelManager.selectedModel.DuplicateModel.ComponentPositionLinks || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formComponentPositionSetpoints">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelSetpoints',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateModelSetpoints',
                            defaultMessage: 'Model Setpoints'
                          }),
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelSetpoints || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formComponentPositionThresholds">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelThresholds',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateModelThresholds',
                            defaultMessage: 'Model Thresholds'
                          }),
                          disable: (!modelManager.selectedModel.DuplicateModel.ComponentPositionLinks || !modelManager.selectedModel.DuplicateModel.ModelSetpoints),
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelThresholds || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formTelemetryLinks">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelTelemetryLinks',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateTelemetryLinks',
                            defaultMessage: 'Telemetry Links'
                          }),
                          disable: !modelManager.selectedModel.DuplicateModel.ComponentPositionLinks,
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelTelemetryLinks || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formPeriodicSummarySettings">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelPeriodicSummarySettings',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicatePeriodicSummarySettings',
                            defaultMessage: 'Periodic Summary Settings'
                          }),
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelPeriodicSummarySettings || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formModelServicePlans">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelServicePlans',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateModelServicePlans',
                            defaultMessage: 'Model Service Plans'
                          }),
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelServicePlans || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formModelSequences">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelSequence',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateSequenceVersion',
                            defaultMessage: 'Current Sequence Version'
                          }),
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelSequence || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formModelUnitOverview">
                    <Checkbox
                      key={uuidv4()}
                      dataArray={[
                        {
                          SKEY: 'ModelDashboardConfig',
                          target: { type: 'checkbox' },
                          label: intl.formatMessage({
                            id: 'model.duplicateModelDashboardConfig',
                            defaultMessage: 'Model Dashboard Configuration'
                          }),
                          isChecked: modelManager.selectedModel.DuplicateModel.ModelDashboardConfig || false
                        }
                      ]}
                      onSelectionChange={onDuplicateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col className={s.modelVariantInfo}>
                  <p>
                    <FormattedMessage
                      id="model.duplicateModelVariantInfo"
                      defaultMessage="Please select the model variant(s) that require to be duplicated"
                    />
                  </p>
                </Col>
              </Row>
              <ModelVariantListTable modelManager={modelManager} setDuplicateChange={setDuplicateChange} intl={intl} />
            </div>
          )}
          {/* Organisation Selection Section */}

          {!IsDuplicate && <div className={s.modelContent}>
            <Row>
              <Col className={s.subHeading}>
                <h4>
                  <FormattedMessage id="model.OrganisationProvisioning" defaultMessage="Organisation Provisioning" />
                </h4>
                <p>
                  <FormattedMessage
                    id="model.selectOrganisations"
                    defaultMessage="Select Organisations that should have access to this model:"
                  />
                </p>
                <p className={s.note}>
                  <FormattedMessage
                    id="model.noteOrganisationProvisioning"
                    defaultMessage="Note: Only one Organisation can be selected for provisioning at a time currently, due to system limitations."
                  />
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                <ModernCheckbox
                  data={OrganisationCheckboxData}
                  columns={['Name', 'IsProvisioned']}
                  columnLabels={{
                    Name: intl.formatMessage({ id: 'model.OrganisationName', defaultMessage: 'Organisation Name' }),
                    IsProvisioned: intl.formatMessage({ id: 'model.OrganisationStatus', defaultMessage: 'Provisioned' })
                  }}
                  selectedKeys={provisionedOrgIds}
                  onSelectionChange={handleOrganisationSelectionChange}
                  onSearchChange={handleSearchChange}
                  searchText={searchText}
                  showSelectAll={false}
                  controlData={{
                    emptyMessage: intl.formatMessage({
                      id: 'model.noOrganisations',
                      defaultMessage: 'No Organisations available'
                    }),
                    emptyResults: intl.formatMessage({
                      id: 'model.noOrganisationsFound',
                      defaultMessage: 'No Organisations found matching your search'
                    }),
                    showTable: true
                  }}
                  disable={modelManager.isLoading}
                />
              </Col>
            </Row>
          </div>}

          <Row>
            <Col>
              <Button
                variant="primary"
                className={classNames(s.margin5, s.btnSaveChanges)}
                onClick={submitForm}
                noValidate
                data-unittest="saveData"
              >
                <FormattedMessage id="model.saveModel" defaultMessage="SAVE CHANGES" />
              </Button>
              <Link to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-list`}>
                <Button variant="outline-secondary" className={s.btnCancel}>
                  <FormattedMessage id="model.cancelModel" defaultMessage="CANCEL" />
                </Button>
              </Link>
            </Col>
          </Row>
        </div>
      </ModelCategoryConfigurationContentTemplate>
    </div>
  );
};





Model.propTypes = {
  saveModel: PropTypes.func.isRequired,
  modelManager: PropTypes.object.isRequired,
  describeModel: PropTypes.func.isRequired,
  changeModel: PropTypes.func.isRequired,
  userProfileWidgets: PropTypes.object.isRequired
};

export default injectIntl(Model);

const ModelVariantListTable = ({ modelManager, setDuplicateChange }) => {
  let versionData = modelManager.selectedModel.DuplicateModel.ModelVariants || [];
  let modelSetpointSelected = modelManager.selectedModel.DuplicateModel.ModelSetpoints || false;
  let modelThresholdSelected = modelManager.selectedModel.DuplicateModel.ModelThresholds || false;
  const onDuplicateChange = updatedObject => {
    versionData.forEach((element, index) => {
      if (element.ModelVariantId === updatedObject.ModelVariantId) {
        versionData[index] = updatedObject;
      }
    });

    setDuplicateChange({
      ...modelManager.selectedModel.DuplicateModel,
      ModelVariants: versionData
    });
  };

  return (
    <div className="">
      <Table variant className={s.wsInnerTable}>
        <thead>
          <tr>
            <th className={s.selectWidth}>
              <FormattedMessage id="model.versionSelected" defaultMessage="Select" />
            </th>
            <th>
              <FormattedMessage id="modelVariant.version" defaultMessage="Version" />
            </th>
            <th>
              <FormattedMessage id="modelVariant.description" defaultMessage="Description" />
            </th>
            <th>
              <FormattedMessage id="model.duplicateModelVariantSetpoints" defaultMessage="Model Variant Setpoints" />
            </th>
            <th>
              <FormattedMessage id="model.duplicateModelVariantThresholds" defaultMessage="Model Variant Tresholds" />
            </th>
          </tr>
        </thead>
        <tbody>
          {versionData.map(n => {
            return <TableRow key={n.ModelVariantId} n={n} onDuplicateModelVariantChange={onDuplicateChange} modelSetpointSelected={modelSetpointSelected} modelThresholdSelected={modelThresholdSelected} />;
          })}
        </tbody>
      </Table>
    </div>
  );
};

const TableRow = ({ n, onDuplicateModelVariantChange, modelSetpointSelected, modelThresholdSelected }) => {
  const onLocalChange = (e, type) => {
    let selectedModelVariant = {};

    selectedModelVariant = { ...n, [e.target.name]: e.target.checked };
    if (e.target.name === 'IsSelected' && !e.target.checked) {
      selectedModelVariant.ModelVariantThresholds = false;
      selectedModelVariant.ModelVariantSetpoints = false;
    }

    onDuplicateModelVariantChange(selectedModelVariant);
  };
  //onDuplicateModelVariantChange

  return (
    <tr>
      <td>
        <Checkbox
          key={uuidv4()}
          dataArray={[
            {
              SKEY: 'IsSelected',
              target: { type: 'checkbox' },
              label: '',
              isChecked: n.IsSelected
            }
          ]}
          onSelectionChange={onLocalChange}
        />
      </td>
      <td>{n.Name || ''}</td>
      <td>{n.Description || ''}</td>
      <td>
        <Checkbox
          key={uuidv4()}
          containerClass={s.alignMiddle}
          dataArray={[
            {
              SKEY: 'ModelVariantSetpoints',
              target: { type: 'checkbox' },
              label: '',
              isChecked: n.ModelVariantSetpoints
            }
          ]}
          disable={!n.IsSelected || !modelSetpointSelected}
          onSelectionChange={onLocalChange}
        />
      </td>
      <td>
        <Checkbox
          key={uuidv4()}
          containerClass={s.alignMiddle}
          dataArray={[
            {
              SKEY: 'ModelVariantThresholds',
              target: { type: 'checkbox' },
              label: '',
              isChecked: n.ModelVariantThresholds
            }
          ]}
          disable={!n.IsSelected || !modelThresholdSelected}
          onSelectionChange={onLocalChange}
        />
      </td>
    </tr>
  );
};
